import { z } from 'zod';
import { BFF_ROUTES } from './routes.js';
/**
 * E-SF16 — the tokenized offer entry (ADR-0010 D4).
 *
 * A CRM agent composes an offer of 1..N tariffs and emails the customer a link.
 * Quotation builds that link as `<offerBaseUrl>/offer/<token>` (`offer-url.ts`,
 * `OFFER_PATH_PREFIX = '/offer/'`), which is a US-SHOP path — the ingress
 * path-routes `/bff/*` to the BFF and `/` to the SPA (ADR-0002 §8), so the
 * emailed link lands in the SPA, not on the BFF.
 *
 * ── WHY THESE CONSTANTS LIVE IN THE CONTRACT ───────────────────────────────
 * `GET /bff/offer/:token` answers with a **302 into the storefront** (D4.1 — never
 * a JSON body). That makes the BFF a WRITER of US-Shop paths for the first time:
 * it has to name the shelf route and the failure route. A hand-copied `'/shop/
 * plans'` in the BFF and a `path: '/shop/plans'` in `router.tsx` are two
 * literals that silently disagree the day either moves, and the symptom would be
 * a customer following an emailed link into the SPA's catch-all redirect. So the
 * paths are declared ONCE, here, where both sides already import their edge from.
 */
/** US-Shop routes the BFF redirects into. Both sides import these — never a literal. */
export const STOREFRONT_PATHS = {
    /**
     * The SPA landing route for an emailed offer link, `/offer/:token`. MUST stay in
     * step with Quotation's `OFFER_PATH_PREFIX` (`offer-url.ts`) — that service
     * builds the URL, this app serves it, and neither can see the other's source.
     */
    offerEntry: '/offer/:token',
    /** The single, indistinguishable failure page for every unusable token (D4 / US-SF16.2). */
    offerUnavailable: '/offer/unavailable',
    /**
     * E-CRM04 — the ONE failure page for every unusable "resume your enrollment"
     * link (unknown, expired, already used, revoked, malformed — Accounts answers an
     * identical 404 for all of them, and so does this page).
     */
    resumeExpired: '/resume/expired',
    /**
     * E-CRM04 — where a resume link lands when this network has opened too many
     * links in the last hour (the BFF's per-IP rule). A top-level navigation must
     * not render a 429 problem+json in the customer's browser, so the BFF
     * redirects here instead. Says "try again shortly" and nothing about the link.
     */
    resumeBusy: '/resume/busy',
    /**
     * E-CRM04 — where a resume link sent to an UNVERIFIED email lands: the lead
     * confirms the phone number they gave before anything is restored.
     */
    resumeConfirm: '/resume/confirm',
    /**
     * E-CRM04 — where every resume link lands first. The link's GET only holds the
     * token; the person presses Continue here, which redeems it. This is what keeps
     * a mail scanner's prefetch from spending the customer's single-use link.
     */
    resumeContinue: '/resume/continue',
    /** The meter-filtered plans shelf the successful entry lands on. */
    shelf: '/shop/plans',
    /**
     * The anonymous enrollment-status page, `/status/:token` — where a SIGNED
     * enrollment lives.
     *
     * Declared here beside the offer paths because it is the same kind of thing: an
     * unauthenticated, token-addressed SPA route that more than one place has to
     * name. It was previously a bare string literal in `router.tsx`, which was
     * survivable while the router was the only namer; the sign step's completion
     * path now navigates here too, and two hand-written copies of a route is how a
     * customer ends up on a 404 after signing a contract.
     */
    status: '/status/:token',
    /**
     * E-SF20 — the anonymous pending-service page, `/new-service/:ref`, where a
     * DECLARED new build lives while it waits for a meter.
     *
     * The same kind of thing as `status` above and declared for the same reason:
     * the BFF builds this URL (it goes in the confirmation email) and the SPA
     * serves it, and neither can see the other's source. Two hand-written copies of
     * a route is how a customer ends up on a 404 after clicking a link we sent.
     *
     * ⚠️ `:ref`, NOT `:token` — a different capability with a different lifetime
     * (months, not days) and a different store. Keeping the segment name distinct
     * keeps the two from being confused at a glance in the router.
     */
    newServiceStatus: '/new-service/:ref',
};
/**
 * The query param carrying the shopper's chosen monthly kWh.
 *
 * ⚠️ THE USAGE IS URL STATE, NOT SESSION STATE, and that is a pre-existing and
 * deliberate decision (`funnel-rehydrate.service.ts`): it makes a priced shelf
 * bookmarkable and shareable, and "no usage in the URL" honestly means "this
 * visitor has not chosen one". The offer entry therefore CANNOT seed the usage
 * into the funnel session and expect the shelf to see it — `MeterPlansPage`
 * guards on `funnel.monthlyUsageKwh`, which is parsed from here. The 302 must
 * carry it, or an offer link lands on the shelf and is bounced to the chooser.
 *
 * The companion `?tier=` is deliberately NOT emitted by the BFF: the tier is
 * display state (which radio is lit) whose boundary rules live in US-Shop's
 * `consumption.ts`, and duplicating those bounds server-side would be a second
 * copy of a rule that has to agree. `parseUsageSelection` treats an absent tier as
 * "unknown", which is exactly true here.
 */
export const STOREFRONT_USAGE_PARAM = 'usage';
/**
 * Marks a load that ARRIVED FROM AN OFFER REDIRECT, so the storefront does not
 * retire the session the BFF just seeded for it.
 *
 * ── THE REGRESSION THIS EXISTS TO CLOSE ────────────────────────────────────
 * `FunnelHydrationGate` mints `GET /bff/session?fresh=1` on every page load
 * (product decision 2026-09-16: nothing is remembered across a refresh), and
 * `fresh=1` DELETES the session the cookie names. An offer arrival is the one
 * case where that is wrong: `/bff/offer/:token` has, moments earlier, minted a
 * brand-new session and seeded the customer's meter, usage profile and identity
 * into it, then redirected here. The gate then deleted exactly that — so the
 * shelf found no meter and bounced the customer to the storefront home page.
 *
 * Every emailed offer link was landing on `/`. Verified end-to-end 2026-09-18:
 * the BFF logged `offer entry: session seeded`, 302'd to `/shop/plans?usage=…`,
 * the SPA called `session?fresh=1` 0.2s later, and `GET /bff/funnel` then
 * reported `candidate: null`.
 *
 * ── WHY THIS DOES NOT REOPEN WHAT 2026-09-16 CLOSED ────────────────────────
 * That decision is about not remembering the LAST VISIT. This session is not a
 * memory of anything — it was created by this navigation, server-side, and holds
 * only what the agent put in the offer. A plain reload of the shelf carries no
 * marker and still starts over.
 *
 * ── NOT A CONTROL ──────────────────────────────────────────────────────────
 * It rides in a URL, so it is attacker-controlled and must never gate anything
 * that matters. All it can do is stop the browser retiring ITS OWN session,
 * which the caller could equally achieve by not calling `fresh=1` at all. The
 * meter it preserves was written server-side from the offer's own quote and is
 * never read from the client.
 */
export const STOREFRONT_OFFER_ARRIVAL_PARAM = 'offer';
/**
 * The offer token as it appears in a URL path segment.
 *
 * Quotation mints `randomBytes(32).toString('base64url')` — 256 bits, 43 URL-safe
 * characters. The bound here is deliberately LOOSE (shape, not exact length): the
 * BFF must not become a token-format oracle, and a caller whose malformed guess
 * is refused DIFFERENTLY from a well-formed unknown one has been told something
 * about the right shape. Anything that passes this is forwarded upstream and
 * fails there identically to an unknown token (US-SF16.2).
 *
 * The cap exists so a multi-kilobyte path segment cannot be forwarded to
 * Quotation at all.
 *
 * ⚠️ IT IS PAIRED WITH FASTIFY'S `maxParamLength` IN THE BFF, AND THE TWO MUST
 * NOT DRIFT. Below the transport's bound, THIS schema does the refusing and every
 * bad token gets the same uniform answer. Above it, the router refuses first with
 * a different status and a body that echoes the caller's input. Keep this value
 * STRICTLY BELOW `maxParamLength` (512), so there is a band in which the
 * application — not the transport — refuses an over-long token, and so the uniform
 * path covers everything a token could plausibly be.
 */
export const OfferTokenSchema = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[A-Za-z0-9_-]+$/u, 'offer token must be URL-safe base64');
/**
 * Build the BFF hand-off URL the SPA landing route navigates to.
 *
 * Exported from the contract (rather than assembled at the call site) because the
 * BFF route path and the SPA's navigation target are the same fact stated twice.
 *
 * ⚠️ The caller must perform a FULL BROWSER NAVIGATION to this URL, never `fetch`.
 * `fetch` follows a 302 transparently and hands back the redirect TARGET's body,
 * which for a hand-off into the storefront is the SPA's own `index.html` — the
 * `Set-Cookie` would land but the browser would never move, so the customer would
 * sit on a blank landing page while the funnel silently waited elsewhere.
 */
export function buildOfferHandoffUrl(token) {
    return BFF_ROUTES.offerEntry.replace(':token', encodeURIComponent(token));
}
//# sourceMappingURL=offer.js.map